import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bot, ChevronDown, ChevronRight, ExternalLink, Globe, Pencil, Play, RefreshCw, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/components/ripeness/radarMeta";

const STATUS_META = {
    found: { label: "Found", tone: "border-emerald-400/40 text-emerald-300" },
    reading: { label: "Reading…", tone: "border-sky-400/40 text-sky-300" },
    not_found: { label: "Not found", tone: "border-slate-500/40 text-slate-400" },
    no_content: { label: "No readable text", tone: "border-amber-400/40 text-amber-300" },
    blocked: { label: "Blocked by site", tone: "border-rose-400/40 text-rose-300" },
    error: { label: "Error", tone: "border-rose-400/40 text-rose-300" },
    ai_disabled: { label: "AI off", tone: "border-slate-500/40 text-slate-400" },
};
const SRC_LABEL = { manual: "set by admin", auto: "found automatically", agent: "found by the finder agent", sec_company: "SEC company record", sec_filings: "from SEC filings", name_guess: "from the name" };
const VIA_LABEL = { browser: "browser headers", reader: "reader proxy (renders JavaScript)", webproxy: "free web proxy", archive: "Internet Archive snapshot", proxy: "HTTP proxy" };
const onHub = (r) => (r?.data?.forwarded ? ` — carried out on the hub (${r.data.processed_on}); the result syncs back in a moment` : "");
const RETRY_STATUSES = ["not_found", "no_content", "blocked", "error"];

function fmtDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function StatusBadge({ status, testId }) {
    const m = STATUS_META[status] || { label: status || "—", tone: "border-slate-600 text-slate-400" };
    return <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider whitespace-nowrap ${m.tone}`} data-testid={testId}>{m.label}</span>;
}

function UrlEditor({ item, onSave, onCancel }) {
    const [url, setUrl] = useState(item.pipeline_url_source === "manual" ? item.pipeline_url || "" : "");
    const [reason, setReason] = useState("");
    const k = item.sponsor_key;
    return (
        <div className="mt-2 flex flex-col md:flex-row gap-2" data-testid={`pipe-editor-${k}`}>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… pipeline page or PDF"
                className="flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 focus:outline-none focus:border-sky-400/60" data-testid={`pipe-url-input-${k}`} />
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (audit)"
                className="md:w-48 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 focus:outline-none focus:border-sky-400/60" data-testid={`pipe-reason-input-${k}`} />
            <button onClick={() => onSave(url, reason)} disabled={!url.trim()} className="rounded bg-emerald-600/80 hover:bg-emerald-600 px-2 py-1 text-xs text-white disabled:opacity-50" data-testid={`pipe-save-btn-${k}`}>Save &amp; read</button>
            <button onClick={onCancel} className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800" data-testid={`pipe-cancel-btn-${k}`}>Cancel</button>
        </div>
    );
}

function RowsTable({ rows }) {
    if (!rows?.length) return <div className="text-xs text-slate-500 italic mt-2">Nothing read from this page yet.</div>;
    return (
        <table className="w-full text-[11px] mt-2">
            <thead className="text-slate-500 text-left"><tr><th className="py-0.5 pr-2 font-medium">Compound</th><th className="pr-2 font-medium">Indication</th><th className="pr-2 font-medium">Phase / stage</th><th className="font-medium">Page says</th></tr></thead>
            <tbody className="text-slate-300">
                {rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-800/70">
                        <td className="py-0.5 pr-2">{r.compound}</td><td className="pr-2">{r.indication || "—"}</td>
                        <td className="pr-2 whitespace-nowrap">{r.phases?.length ? `P${r.phases.join("/")}` : r.stage}{r.planned ? " · planned" : ""}{r.discontinued ? " · discontinued" : ""}</td>
                        <td className="text-slate-500 truncate max-w-[280px]" title={r.quote}>{r.quote || "—"}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function AgentLine({ item }) {
    const k = item.sponsor_key;
    if (item.agent_running) return <div className="text-[11px] text-sky-300 mt-0.5 flex items-center gap-1" data-testid={`pipe-agent-${k}`}><Bot className="w-3 h-3 animate-pulse" /> Finder agent is working on this sponsor…</div>;
    if (!item.agent_checked_at && !item.fetch_via) return null;
    return (
        <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`pipe-agent-${k}`}>
            <Bot className="w-3 h-3 text-violet-300" />
            {item.agent_checked_at ? <span>Agent re-evaluated {fmtDateTime(item.agent_checked_at)}</span> : null}
            {item.agent_next_check_at ? <span data-testid={`pipe-agent-next-${k}`}>· next look {fmtDateTime(item.agent_next_check_at)}</span> : (item.agent_checked_at ? <span>· no further look needed</span> : null)}
            {item.agent_last_result ? <span className="text-slate-500" data-testid={`pipe-agent-result-${k}`}>· {item.agent_last_result}</span> : null}
            {item.fetch_via && item.fetch_via !== "direct" ? (
                <span className="rounded-full border border-violet-400/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-violet-300" data-testid={`pipe-via-${k}`}>
                    read via {VIA_LABEL[item.fetch_via] || item.fetch_via}{item.page_snapshot_at ? ` · ${fmtDate(item.page_snapshot_at)}` : ""}</span>
            ) : null}
        </div>
    );
}

function SponsorRow({ item, onSet, onReread, onAgent }) {
    const [edit, setEdit] = useState(false);
    const [open, setOpen] = useState(false);
    const k = item.sponsor_key;
    return (
        <li className="border-t border-slate-800 py-2" data-testid={`pipe-row-${k}`}>
            <div className="flex items-start gap-2 flex-wrap">
                <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-200 mt-0.5" data-testid={`pipe-expand-${k}`}>{open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-slate-100 font-medium">{item.sponsor_name}</span>
                        <StatusBadge status={item.status} testId={`pipe-status-${k}`} />
                        <span className="text-[11px] text-slate-500">{item.rows_count} rows · {item.programs} program{item.programs === 1 ? "" : "s"} · checked {fmtDate(item.checked_at)}</span>
                    </div>
                    {item.pipeline_url ? (
                        <a href={item.pipeline_url} target="_blank" rel="noreferrer" className="text-xs text-sky-300 hover:underline inline-flex items-center gap-1 max-w-full" data-testid={`pipe-url-${k}`}>
                            <span className="truncate">{item.pipeline_url}</span><ExternalLink className="w-2.5 h-2.5 shrink-0" />
                        </a>
                    ) : <span className="text-xs text-slate-500">No pipeline page{item.website ? ` · website ${item.website}` : ""}</span>}
                    {item.pipeline_url_source ? <span className="ml-2 text-[10px] text-slate-500">({SRC_LABEL[item.pipeline_url_source] || item.pipeline_url_source})</span> : null}
                    {item.error ? <div className="text-[11px] text-amber-300 mt-0.5" data-testid={`pipe-error-${k}`}>{item.error}</div> : null}
                    <AgentLine item={item} />
                </div>
                <div className="flex items-center gap-1.5">
                    <button onClick={() => setEdit(!edit)} className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-200 hover:bg-slate-800" data-testid={`pipe-edit-btn-${k}`}><Pencil className="w-3 h-3" /> Fix URL</button>
                    {item.pipeline_url_source === "manual" ? (
                        <button onClick={() => onSet(item, null, "back to automatic discovery")} className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 hover:bg-slate-800" title="Forget the admin URL and discover again" data-testid={`pipe-auto-btn-${k}`}><RotateCcw className="w-3 h-3" /> Auto</button>
                    ) : null}
                    {RETRY_STATUSES.includes(item.status) ? (
                        <button onClick={() => onAgent(item)} disabled={item.agent_running} title="Research the website / pipeline page and try other ways of reading it (browser headers, reader proxy, free web proxy, Internet Archive)"
                            className="inline-flex items-center gap-1 rounded border border-violet-400/40 px-2 py-0.5 text-[11px] text-violet-200 hover:bg-violet-400/10 disabled:opacity-50" data-testid={`pipe-agent-btn-${k}`}>
                            <Bot className={`w-3 h-3 ${item.agent_running ? "animate-pulse" : ""}`} /> Agent
                        </button>
                    ) : null}
                    <button onClick={() => onReread(item)} disabled={item.status === "reading"} className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-200 hover:bg-slate-800 disabled:opacity-50" data-testid={`pipe-reread-btn-${k}`}>
                        <RefreshCw className={`w-3 h-3 ${item.status === "reading" ? "animate-spin" : ""}`} /> Re-read now
                    </button>
                </div>
            </div>
            {edit ? <UrlEditor item={item} onCancel={() => setEdit(false)} onSave={async (u, r) => { if (await onSet(item, u, r)) setEdit(false); }} /> : null}
            {open ? <RowsTable rows={item.rows} /> : null}
        </li>
    );
}

/** Pipeline finder agent: daily research on not-found / unreadable / blocked pages — status + manual start. */
function FinderPanel({ onRunStarted, reloadList, rowsActive, pollKey }) {
    const [st, setSt] = useState(null);
    const [busy, setBusy] = useState(false);
    const wasRunning = useRef(false);
    const load = useCallback(async () => {
        try { const r = await api.get("/v1/ripeness/sponsor-pipelines/finder"); setSt(r.data); } catch (e) { /* shown in the list toast */ }
    }, []);
    useEffect(() => { load(); }, [load, pollKey]);
    useEffect(() => {
        if (!st?.running && !rowsActive) { if (wasRunning.current) { wasRunning.current = false; reloadList(); load(); } return undefined; }
        wasRunning.current = true;
        const id = setInterval(() => { load(); reloadList(); }, 5000);
        return () => clearInterval(id);
    }, [st?.running, rowsActive, load, reloadList]);
    const run = async () => {
        setBusy(true);
        try {
            const r = await api.post("/v1/ripeness/sponsor-pipelines/finder/run");
            toast.success(`Finder agent started on ${r.data.sponsors} sponsor${r.data.sponsors === 1 ? "" : "s"}${onHub(r)}`);
            onRunStarted?.(); setTimeout(load, 800);
        } catch (e) { toast.error(`Could not start the agent: ${formatApiErrorDetail(e)}`); } finally { setBusy(false); }
    };
    if (!st) return null;
    const lr = st.last_run;
    const still = lr?.still ? Object.entries(lr.still).map(([k, v]) => `${v} ${STATUS_META[k]?.label?.toLowerCase() || k}`).join(", ") : "";
    return (
        <div className="mt-3 rounded border border-violet-400/30 bg-violet-400/5 px-3 py-2 text-xs" data-testid="pipe-finder-panel">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1.5 text-violet-200 font-medium"><Bot className={`w-3.5 h-3.5 ${st.running ? "animate-pulse" : ""}`} /> Pipeline finder agent</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${st.running ? "border-sky-400/40 text-sky-300" : "border-slate-600 text-slate-400"}`} data-testid="pipe-finder-status">
                    {st.running ? `working${st.current ? ` · ${st.current}` : ""}` : st.managed_here ? "idle" : `idle · runs on the hub${st.hub ? ` (${st.hub})` : ""}`}</span>
                <span className="text-slate-400" data-testid="pipe-finder-counts">{st.eligible} sponsor{st.eligible === 1 ? "" : "s"} to work on · {st.due} due at the next daily pass</span>
                <span className="text-slate-500" data-testid="pipe-finder-next">next daily pass {fmtDateTime(st.next_run_at)} ({String(st.hour_utc).padStart(2, "0")}:00 UTC)</span>
                <button onClick={run} disabled={busy || st.running} title={st.managed_here ? "" : "Starts the pass on the hub — results sync back here"}
                    className="ml-auto inline-flex items-center gap-1 rounded bg-violet-600/80 hover:bg-violet-600 px-2.5 py-1 text-[11px] text-white disabled:opacity-50" data-testid="pipe-finder-run-btn">
                    <Play className="w-3 h-3" /> Run agent now{st.managed_here ? "" : " on the hub"}
                </button>
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
                Daily, on every sponsor whose page is <em>not found</em>, has <em>no readable text</em>, is <em>blocked by the site</em> or in <em>error</em>: finds the company website and pipeline page
                (Wikidata, Claude, homepage links, common paths, pipeline PDFs) and tries other ways of reading — browser headers, a reader proxy that renders JavaScript, a free web proxy (AnyProxy), the Internet Archive's latest snapshot
                {st.strategies?.some((s) => s.key === "proxy" && s.available) ? ", an HTTP proxy" : ""}. Not found → looked at again after {st.intervals?.not_found_days} days; unreadable → every day.
                {st.managed_here ? "" : " On this environment the agent does not run itself: the hub does the research and its results arrive by Radar Sync."}
            </div>
            {lr ? (
                <div className="text-[11px] text-slate-500 mt-1" data-testid="pipe-finder-last-run">
                    Last pass {fmtDateTime(lr.finished_at)} ({lr.trigger}{lr.interrupted ? ", interrupted by a restart" : ""}): {lr.evaluated} re-evaluated · <span className="text-emerald-300">{lr.found} now readable</span>{lr.read_failed ? ` · ${lr.read_failed} readable but not parsed` : ""}{still ? ` · still ${still}` : ""}{lr.errors ? ` · ${lr.errors} agent errors` : ""} · ${Number(lr.cost_usd || 0).toFixed(2)}
                </div>
            ) : null}
            {st.running && st.current_run ? (
                <div className="text-[11px] text-sky-300/80 mt-1" data-testid="pipe-finder-progress">
                    This pass: {st.current_run.evaluated} re-evaluated · {st.current_run.found} now readable{Object.keys(st.current_run.still || {}).length ? ` · still ${Object.entries(st.current_run.still).map(([k, v]) => `${v} ${STATUS_META[k]?.label?.toLowerCase() || k}`).join(", ")}` : ""}
                </div>
            ) : null}
            {!st.running && st.last_single_run ? (
                <div className="text-[11px] text-slate-500 mt-1" data-testid="pipe-finder-last-single">
                    Last single-sponsor run {fmtDateTime(st.last_single_run.finished_at)}: {st.last_single_run.sponsor_key} — {st.last_single_run.found ? "now readable" : "still unresolved"} · ${Number(st.last_single_run.cost_usd || 0).toFixed(2)}
                </div>
            ) : null}
        </div>
    );
}

function useSponsorPipelines(open) {
    const [q, setQ] = useState({ search: "", status: "" });
    const [data, setData] = useState(null);
    const [limit, setLimit] = useState(50);
    const timer = useRef(null);
    const load = useCallback(async () => {
        try {
            const r = await api.get("/v1/ripeness/sponsor-pipelines", { params: { search: q.search || undefined, status: q.status || undefined, limit } });
            setData(r.data);
        } catch (e) { toast.error(`Sponsor pipelines failed: ${formatApiErrorDetail(e)}`); }
    }, [q, limit]);
    useEffect(() => {
        if (!open) return undefined;
        clearTimeout(timer.current);
        timer.current = setTimeout(load, 250);
        return () => clearTimeout(timer.current);
    }, [open, load]);
    const reading = data?.items?.some((i) => i.status === "reading" || i.agent_running);
    useEffect(() => {
        if (!open || !reading) return undefined;
        const id = setInterval(load, 4000);
        return () => clearInterval(id);
    }, [open, reading, load]);
    return { q, setQ, data, load, limit, setLimit, reading: !!reading };
}

/** Sponsor pipeline pages: what was found / read, and admin corrections. */
export function SponsorPipelinesDialog({ open, onClose }) {
    const s = useSponsorPipelines(open);
    const setUrl = useCallback(async (item, url, reason) => {
        try {
            const r = await api.put(`/v1/ripeness/sponsor-pipelines/${encodeURIComponent(item.sponsor_key)}`, { pipeline_url: url, reason: reason || "" });
            toast.success((url ? `Saved — reading ${item.sponsor_name}'s page now` : "Back to automatic discovery") + onHub(r));
            s.load();
            return true;
        } catch (e) { toast.error(`Save failed: ${formatApiErrorDetail(e)}`); return false; }
    }, [s]);
    const reread = useCallback(async (item) => {
        try {
            const r = await api.post(`/v1/ripeness/sponsor-pipelines/${encodeURIComponent(item.sponsor_key)}/refresh`);
            toast.info(`Reading ${item.sponsor_name}'s pipeline page…${onHub(r)}`);
            s.load();
        } catch (e) { toast.error(`Re-read failed: ${formatApiErrorDetail(e)}`); }
    }, [s]);
    const [agentKey, setAgentKey] = useState(0);
    const agent = useCallback(async (item) => {
        try {
            const r = await api.post(`/v1/ripeness/sponsor-pipelines/${encodeURIComponent(item.sponsor_key)}/finder`);
            toast.info(`Finder agent is working on ${item.sponsor_name}…${onHub(r)}`);
            setTimeout(() => { s.load(); setAgentKey((n) => n + 1); }, 600);
        } catch (e) { toast.error(`Agent could not start: ${formatApiErrorDetail(e)}`); }
    }, [s]);
    if (!open) return null;
    const counts = s.data?.status_counts || {};
    return (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/60 p-4 overflow-y-auto" data-testid="pipe-dialog">
            <div className="w-full max-w-5xl rounded-lg border border-slate-700/60 bg-slate-900/95 p-6 shadow-2xl my-6">
                <div className="flex items-center justify-between border-b border-slate-700/60 pb-3">
                    <h3 className="text-lg font-semibold text-slate-100 inline-flex items-center gap-2"><Globe className="w-5 h-5 text-sky-400" /> Sponsor pipeline pages</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-200" data-testid="pipe-close-btn"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Found automatically (SEC company record, sponsor filings, common paths like /pipeline) and read by Claude. A URL you set is kept over discovery; tracks use it at their next recheck or with “Check new sources now”.</p>
                <FinderPanel reloadList={s.load} onRunStarted={s.load} rowsActive={s.reading} pollKey={agentKey} />
                <div className="flex flex-col md:flex-row gap-2 mt-3">
                    <input value={s.q.search} onChange={(e) => s.setQ({ ...s.q, search: e.target.value })} placeholder="Search sponsor or URL"
                        className="flex-1 rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-sky-400/60" data-testid="pipe-search-input" />
                    <div className="flex flex-wrap gap-1" data-testid="pipe-status-filters">
                        {[["", "All"], ...Object.keys(STATUS_META).filter((k) => counts[k]).map((k) => [k, STATUS_META[k].label])].map(([k, l]) => (
                            <button key={k || "all"} onClick={() => s.setQ({ ...s.q, status: k })} data-testid={`pipe-filter-${k || "all"}`}
                                className={`rounded-full border px-2 py-1 text-[11px] transition-colors ${s.q.status === k ? "border-sky-400/60 text-sky-200 bg-sky-400/10" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
                                {l} <span className="text-slate-500">{k ? counts[k] : Object.values(counts).reduce((a, b) => a + b, 0)}</span>
                            </button>
                        ))}
                    </div>
                </div>
                {!s.data ? <div className="text-xs text-slate-500 mt-3">Loading…</div> : null}
                {s.data && !s.data.items.length ? <div className="text-xs text-slate-500 mt-3 italic" data-testid="pipe-empty">No sponsors yet — they appear as the one-time run or rechecks reach their compounds.</div> : null}
                <ul className="mt-2" data-testid="pipe-list">{(s.data?.items || []).map((it) => <SponsorRow key={it.sponsor_key} item={it} onSet={setUrl} onReread={reread} onAgent={agent} />)}</ul>
                {s.data && s.data.total > s.data.items.length && s.limit < 200 ? (
                    <button onClick={() => s.setLimit(Math.min(200, s.limit + 50))} className="mt-2 text-xs text-sky-300 hover:underline" data-testid="pipe-more-btn">Show more ({s.data.total - s.data.items.length} left)</button>
                ) : null}
            </div>
        </div>
    );
}
